import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, X } from "lucide-react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useQuery, useMutation } from "@tanstack/react-query";
import { BASE_URL } from "utils/constant";

const OrganizationDetailsForm = () => {
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showNamePopup, setShowNamePopup] = useState(true);
  const [showLogoPopup, setShowLogoPopup] = useState(false);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempImageType, setTempImageType] = useState("image/jpeg");
  const [tempImageName, setTempImageName] = useState("photo.jpg");
  const imgRef = React.useRef(null);
  const accessToken = localStorage.getItem("access");

  // Helper function to trim filename if it exceeds 100 characters
  const trimFileName = (file) => {
    if (file.name.length <= 100) {
      return file;
    }

    const extension = file.name.split(".").pop();
    const nameWithoutExtension = file.name.substring(
      0,
      file.name.lastIndexOf(".")
    );
    const maxNameLength = 100 - extension.length - 1; // -1 for the dot
    const trimmedName = nameWithoutExtension.substring(0, maxNameLength);

    // Create a new file with trimmed name
    const trimmedFile = new File([file], `${trimmedName}.${extension}`, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return trimmedFile;
  };

  const getCropFileMeta = (file) => {
    if (file.type !== "image/svg+xml") {
      return {
        type: file.type || "image/jpeg",
        name: file.name || "photo.jpg",
      };
    }

    const baseName = (file.name || "photo").replace(/\.[^/.]+$/, "");
    return {
      type: "image/png",
      name: `${baseName}.png`,
    };
  };

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["user-details"],
    queryFn: async () => {
      const { data } = await axios.get(
        `${BASE_URL}/auth/user-details-of-user/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken?.trim()}`,
          },
        }
      );
      return data;
    },
    enabled: !!accessToken,
  });

  const createOrganization = useMutation({
    mutationFn: async ({ userId, orgName, logoFile }) => {
      const form = new FormData();
      form.append("owner", userId);
      form.append("organization_name", orgName);
      form.append("active_flag", true);
      if (logoFile) {
        // Trim filename before appending
        const trimmedFile = trimFileName(logoFile);
        form.append("logo_image_file", trimmedFile);
      }

      const { data } = await axios.post(
        `${BASE_URL}/org/create-organization/`,
        form,
        {
          headers: {
            Authorization: `Bearer ${accessToken.trim()}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return data;
    },
    onSuccess: (data) => {
      localStorage.setItem("organization", JSON.stringify(data.data));
      toast.success("Organization created successfully");
      navigate("/");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create organization"
      );
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (optional - e.g., 5MB limit)
      const maxSize = 15 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast.error("File size should be less than 15MB");
        return;
      }

      const cropFileMeta = getCropFileMeta(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageType(cropFileMeta.type);
        setTempImageName(cropFileMeta.name);
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
      if (e.target) {
        e.target.value = "";
      }

      // Show filename info if it was trimmed
      if (file.name.length > 100) {
        const trimmedFile = trimFileName(file);
        toast.info(`Filename was trimmed to: ${trimmedFile.name}`);
      }
    }
  };

  const handleRemoveImage = () => {
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleAdjustPhoto = () => {
    if (logoFile) {
      setTempImageType(logoFile.type || "image/jpeg");
      setTempImageName(logoFile.name || "photo.jpg");
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(logoFile);
    } else if (logoPreview) {
      setTempImageType("image/jpeg");
      setTempImageName("photo.jpg");
      setTempImageSrc(logoPreview);
      setIsCropperOpen(true);
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc,
    pixelCrop,
    mimeType = "image/jpeg"
  ) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const renderedRect = imgRef.current?.getBoundingClientRect();
    const renderedWidth = renderedRect?.width || imgRef.current?.width || image.width;
    const renderedHeight =
      renderedRect?.height || imgRef.current?.height || image.height;

    const scaleX = naturalWidth / renderedWidth;
    const scaleY = naturalHeight / renderedHeight;

    const outputWidth = Math.max(1, Math.floor(pixelCrop.width));
    const outputHeight = Math.max(1, Math.floor(pixelCrop.height));
    const sx = Math.max(
      0,
      Math.min(naturalWidth - 1, Math.floor(pixelCrop.x * scaleX))
    );
    const sy = Math.max(
      0,
      Math.min(naturalHeight - 1, Math.floor(pixelCrop.y * scaleY))
    );
    const sourceWidth = Math.max(
      1,
      Math.min(naturalWidth - sx, Math.floor(pixelCrop.width * scaleX))
    );
    const sourceHeight = Math.max(
      1,
      Math.min(naturalHeight - sy, Math.floor(pixelCrop.height * scaleY))
    );

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(
      image,
      sx,
      sy,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        mimeType,
        0.92
      );
    });
  };

  const handleCropCancel = () => {
    setIsCropperOpen(false);
    setCompletedCrop(null);
    setTempImageSrc(null);
    setCrop(undefined);
  };

  const handleCropConfirm = async () => {
    try {
      const normalizeToPixelCrop = (cropValue) => {
        if (!cropValue) return null;

        if (cropValue.unit === "%") {
          const renderedRect = imgRef.current?.getBoundingClientRect();
          const renderedWidth = renderedRect?.width || imgRef.current?.width || 0;
          const renderedHeight =
            renderedRect?.height || imgRef.current?.height || 0;
          if (!renderedWidth || !renderedHeight) return null;

          return {
            unit: "px",
            x: (cropValue.x / 100) * renderedWidth,
            y: (cropValue.y / 100) * renderedHeight,
            width: (cropValue.width / 100) * renderedWidth,
            height: (cropValue.height / 100) * renderedHeight,
          };
        }

        return cropValue;
      };

      const pixelCrop = normalizeToPixelCrop(completedCrop || crop);
      if (!tempImageSrc || !pixelCrop || !pixelCrop.width || !pixelCrop.height)
        return;

      const blob = await getCroppedImg(tempImageSrc, pixelCrop, tempImageType);
      const croppedFile = new File([blob], tempImageName, {
        type: tempImageType,
      });
      setLogoFile(croppedFile);

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        handleCropCancel();
      };
      reader.readAsDataURL(croppedFile);
    } catch (err) {
      console.error("Cropping failed", err);
      toast.error("Failed to apply crop");
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    if (!width || !height) return;
    setCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
    setCompletedCrop({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
  };

  const handleNext = () => {
    if (orgName.trim() === "") {
      toast.error("Organization name is required");
      return;
    }
    setShowNamePopup(false);
    setShowLogoPopup(true);
  };

  const handleSave = () => {

    if (!userData?.data?.id || orgName.trim() === "") {
      toast.error("Missing required information");
      return;
    }
    // console.log("Saving organization...");
    createOrganization.mutate({
      userId: userData.data.user,
      orgName,
      logoFile,
    });
  };

  return (
    <>
      {showNamePopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
            <h2 className="text-xl font-semibold text-center mb-6">
              Your Organization / Company Details
            </h2>
            {isUserLoading && (
              <p className="text-sm text-gray-500 text-center mb-4">
                Loading user details...
              </p>
            )}

            <label className="block text-sm font-medium mb-2" htmlFor="orgName">
              Organization Name
            </label>
            <input
              id="orgName"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="enter name"
              className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-600 mb-6"
            />

            <button
              onClick={handleNext}
              className="w-full flex justify-center items-center bg-purple-700 hover:bg-purple-800 text-white py-2 rounded-md font-medium transition"
            >
              Next
              <span className="ml-2 text-lg">✔</span>
            </button>
          </div>
        </div>
      )}

      {showLogoPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8">
            <h2 className="text-xl font-semibold text-center mb-6">
              Almost there!
            </h2>

            <label className="block text-sm font-medium mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={orgName}
              readOnly
              className="w-full px-4 py-2 border rounded-md bg-gray-100 mb-6"
            />

            <label className="block text-sm font-medium mb-2">
              Organization Logo / Photo (Optional)
            </label>

            <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-md bg-gray-100 min-h-[150px] mb-6 relative">
              {logoPreview ? (
                <div className="relative w-full h-full flex items-center justify-center p-4">
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    className="max-w-full max-h-32 object-contain rounded"
                  />
                  <button
                    onClick={handleAdjustPhoto}
                    className="absolute top-2 right-10 bg-white text-gray-800 border border-gray-300 px-2 py-1 rounded-md hover:bg-gray-100 text-xs"
                    type="button"
                  >
                    Adjust
                  </button>
                  <button
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 transition-colors"
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer py-8">
                  <Upload className="h-6 w-6 text-gray-500 mb-1" />
                  <span className="text-sm text-gray-600">Upload</span>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/svg+xml, image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {logoFile && (
              <div className="mb-4 p-2 bg-blue-50 rounded text-sm text-blue-700">
                <strong>Selected file:</strong>
                <br />
                <strong>Size:</strong>{" "}
                {(logoFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={isUserLoading || createOrganization.isPending}
              className="w-full flex justify-center items-center bg-purple-700 hover:bg-purple-800 text-white py-2 rounded-md font-medium transition disabled:opacity-50"
            >
              {createOrganization.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Save
              <span className="ml-2 text-lg">✔</span>
            </button>
          </div>
        </div>
      )}

      {isCropperOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={handleCropCancel}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] max-w-xl h-[60vh] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-base font-semibold">Adjust Photo</h4>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative flex-1 bg-gray-100 overflow-auto">
              {tempImageSrc && (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    keepSelection
                    className="max-w-full max-h-full"
                  >
                    <img
                      ref={imgRef}
                      src={tempImageSrc}
                      alt="to-crop"
                      onLoad={onImageLoad}
                      className="max-h-[45vh] max-w-full w-auto h-auto object-contain"
                    />
                  </ReactCrop>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-4 py-2 rounded-md bg-[#3B0069] text-white hover:bg-purple-700"
                type="button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrganizationDetailsForm;
